import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  ScanLine, Thermometer, CheckCircle2, AlertTriangle, Trash2, XCircle,
  Package2, Loader2, MapPin, Clock, History, Camera,
} from "lucide-react";
import CameraScanner from "../components/scan/CameraScanner";
import { tryUnmojibakeScan, unmojibakeWithDiagnostic } from "../lib/scan-unmojibake";

const BARQUETTES_PER_EMP = 4;

// Format compact des emplacements
function emplacementLabel(emp, barq) {
  if (emp == null) return "—";
  return barq ? `E${emp}·B${barq}` : `E${emp}`;
}

function dlcBadge(daysLeft) {
  if (daysLeft == null) return null;
  if (daysLeft < 0) return { label: `DLC -${Math.abs(daysLeft)}j`, color: "#E54D2E", bg: "rgba(229,77,46,0.15)" };
  if (daysLeft <= 1) return { label: `DLC J+${daysLeft}`, color: "#E54D2E", bg: "rgba(229,77,46,0.12)" };
  if (daysLeft <= 3) return { label: `DLC ${daysLeft}j`, color: "#C1A063", bg: "rgba(193,160,99,0.12)" };
  return { label: `DLC ${daysLeft}j`, color: "#88A066", bg: "rgba(136,160,102,0.12)" };
}

export default function ScanAppro() {
  // --- États principaux
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [lots, setLots] = useState([]); // lots du distributeur sélectionné
  const [temperature, setTemperature] = useState("");
  const [savingTemp, setSavingTemp] = useState(false);
  const [scanLog, setScanLog] = useState([]); // historique session
  const [pendingScan, setPendingScan] = useState(null); // résultat /scan/resolve en attente de placement
  const [targetEmp, setTargetEmp] = useState("");
  const [scanInput, setScanInput] = useState("");
  const [resolving, setResolving] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const scanFieldRef = useRef(null);
  // Phase 87as — Ref pour focus auto sur l'emplacement dès qu'un pendingScan arrive
  const empFieldRef = useRef(null);
  useEffect(() => {
    if (pendingScan && empFieldRef.current) {
      // Délai 100ms pour laisser le DOM se monter complètement
      setTimeout(() => {
        empFieldRef.current?.focus();
        empFieldRef.current?.select();
      }, 120);
    }
  }, [pendingScan]);

  // --- Chargements initiaux
  useEffect(() => {
    api.get("/distributors").then((r) => {
      const autos = r.data.filter((d) => d.type === "AUTOMATIQUE");
      setDistributors(autos);
    });
  }, []);

  // --- Recharge les lots quand le distributeur change
  const reloadLots = async (distId) => {
    if (!distId) { setLots([]); return; }
    const r = await api.get(`/lots?active_only=true&distributor_id=${encodeURIComponent(distId)}`);
    const filtered = r.data.filter((l) => l.distributor_id === distId && (l.quantity || 0) > 0);
    setLots(filtered);
  };
  useEffect(() => { reloadLots(distributorId); }, [distributorId]);

  // --- Focus automatique sur le champ scan (mode HID)
  // Phase 87as — En pause tant qu'un scan est en attente de placement (pendingScan)
  // ou que la caméra est ouverte → permet de modifier l'emplacement sans que le focus
  // soit volé toutes les 500ms.
  useEffect(() => {
    if (pendingScan || cameraOpen) return;
    const interval = setInterval(() => {
      if (scanFieldRef.current && document.activeElement !== scanFieldRef.current) {
        // Ne pas voler le focus si un input/select est actif (saisie température, emplacement)
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "BUTTON" || active.tagName === "SELECT" || active.tagName === "TEXTAREA")) return;
        scanFieldRef.current.focus();
      }
    }, 500);
    return () => clearInterval(interval);
  }, [pendingScan, cameraOpen]);

  // --- Calcul emplacement libre auto-suggéré
  const nextFreeSlot = useMemo(() => {
    if (!distributorId) return null;
    const dist = distributors.find((d) => d.id === distributorId);
    if (!dist) return null;
    const emps = dist.emplacements || [];
    for (const emp of emps) {
      const lotsHere = lots.filter((l) => l.emplacement_number === emp);
      const takenBarq = new Set(lotsHere.map((l) => l.barquette_index));
      for (let b = 1; b <= BARQUETTES_PER_EMP; b++) {
        if (!takenBarq.has(b)) return { emp, barq: b };
      }
    }
    return null;
  }, [distributorId, distributors, lots]);

  // --- Cœur de la résolution scan (réutilisé par formulaire HID + caméra)
  const resolveScan = async (raw) => {
    let cleaned = (raw || "").trim();
    if (!cleaned) return;

    // Phase 86e — Filet de sécurité scanner mal configuré (clavier US sur Windows FR/CH-FR).
    // Si le payload semble mojibaké, on tente une correction automatique.
    const diag = unmojibakeWithDiagnostic(cleaned);
    if (diag.wasCorrected) {
      cleaned = diag.corrected;
      toast.warning(
        "Scanner configuré en clavier US — payload corrigé automatiquement. Pour un résultat fiable, reconfigurez votre scanner en AZERTY (manuel Inateck).",
        { duration: 5000 },
      );
    }

    setResolving(true);
    try {
      const r = await api.post("/scan/resolve", { payload: cleaned });
      setPendingScan(r.data);
      if (nextFreeSlot) setTargetEmp(String(nextFreeSlot.emp));
      toast.success(`${r.data.product?.name || "Lot reconnu"}`, { duration: 1500 });
    } catch (err) {
      // Phase 86e — Message dédié si on a tenté un remap qui n'a pas suffi
      const msg = diag.wasCorrected
        ? "Scanner mal configuré (clavier US) — la correction automatique n'a pas suffi. Reconfigurez votre scanner en AZERTY/français."
        : (err?.response?.data?.detail || "QR illisible");
      toast.error(msg, { duration: diag.wasCorrected ? 8000 : 4000 });
      setScanLog((log) => [{ ts: Date.now(), type: "error", msg, raw: cleaned.slice(0, 30) }, ...log]);
    } finally {
      setResolving(false);
    }
  };

  // --- Soumission scan (Enter détecté en mode HID)
  const onScanSubmit = async (e) => {
    e.preventDefault();
    const raw = scanInput;
    setScanInput("");
    await resolveScan(raw);
  };

  // --- Callback caméra
  const onCameraDetected = async (code) => {
    setCameraOpen(false);
    await resolveScan(code);
  };

  // --- Confirme la placement
  const confirmPlacement = async () => {
    if (!pendingScan || !distributorId) return;
    const emp = parseInt(targetEmp, 10);
    if (!emp) { toast.error("Emplacement requis"); return; }
    try {
      const r = await api.post("/transfers", {
        lot_id: pendingScan.lot.id,
        to_distributor_id: distributorId,
        quantity: 1,
        to_emplacement_number: emp,
        reason: "Scan terrain — approvisionnement",
      });
      const placement = `${pendingScan.product?.name} → E${emp}`;
      toast.success(placement);
      setScanLog((log) => [{
        ts: Date.now(), type: "place", msg: placement,
        emp, name: pendingScan.product?.name,
      }, ...log]);
      setPendingScan(null);
      setTargetEmp("");
      await reloadLots(distributorId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur placement");
    }
  };

  // --- Marquer slot vide (réconciliation)
  const markEmpty = async (lot) => {
    if (!window.confirm(`Marquer ${emplacementLabel(lot.emplacement_number, lot.barquette_index)} comme vide ?\n\nUn mouvement « Écart inventaire » sera créé.`)) return;
    try {
      await api.post("/scan/mark-empty", { lot_id: lot.id });
      toast.success(`${emplacementLabel(lot.emplacement_number, lot.barquette_index)} marqué vide`);
      setScanLog((log) => [{
        ts: Date.now(), type: "empty",
        msg: `Écart : ${emplacementLabel(lot.emplacement_number, lot.barquette_index)} libéré`,
      }, ...log]);
      await reloadLots(distributorId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // --- Sauvegarde température
  const saveTemperature = async () => {
    if (!distributorId) return;
    const t = parseFloat(temperature);
    if (isNaN(t)) { toast.error("Température invalide"); return; }
    setSavingTemp(true);
    try {
      await api.post("/scan/temperature", { distributor_id: distributorId, temperature_c: t });
      const ok = t <= 5.0;
      toast[ok ? "success" : "error"](`Température ${t.toFixed(1)}°C ${ok ? "OK" : "HORS PLAGE (>5°C)"}`);
      setScanLog((log) => [{
        ts: Date.now(), type: "temp", msg: `Relevé ${t.toFixed(1)}°C ${ok ? "✓" : "⚠"}`,
      }, ...log]);
      setTemperature("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSavingTemp(false);
    }
  };

  const dist = distributors.find((d) => d.id === distributorId);
  const grid = dist?.emplacements || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2ECE4] font-body" data-testid="scan-appro-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-30">
        <div className="flex items-center gap-3">
          <ScanLine className="w-6 h-6 text-[#C1A063]" />
          <div>
            <h1 className="text-lg font-heading">Scan terrain — Approvisionnement</h1>
            <p className="text-xs text-[#737373] uppercase tracking-wider">POC · Lecteur Bluetooth ou caméra</p>
          </div>
        </div>
        <div className="text-xs text-[#737373] uppercase tracking-wider flex items-center gap-3">
          <span>{scanLog.filter((l) => l.type === "place").length} posées · {scanLog.filter((l) => l.type === "empty").length} écarts</span>
          <Link to="/scan/temperatures" className="flex items-center gap-1 hover:text-[#C1A063] transition-colors" data-testid="link-temp-history">
            <History className="w-3 h-3" /> Historique T°
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
        {/* Colonne gauche : sélection + scan + grille */}
        <div className="lg:col-span-8 space-y-6">

          {/* Sélecteur distributeur */}
          <div className="bg-[#141414] border border-white/10 rounded p-4">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 block">1 · Choisir le distributeur visité</label>
            <Select value={distributorId} onValueChange={setDistributorId}>
              <SelectTrigger data-testid="select-distributor" className="bg-[#0A0A0A] border-white/15 text-[#F2ECE4] h-12 text-base">
                <SelectValue placeholder="Sélectionner un distributeur…" />
              </SelectTrigger>
              <SelectContent>
                {distributors.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`dist-opt-${d.id}`}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Température + Scanner — visible uniquement si distributeur sélectionné */}
          {distributorId && (
            <>
              {/* Température */}
              <div className="bg-[#141414] border border-white/10 rounded p-4">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 block flex items-center gap-2">
                  <Thermometer className="w-3 h-3" /> 2 · Température armoire (pistolet laser)
                </label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    step="0.1"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="ex: 3.2"
                    className="bg-[#0A0A0A] border-white/15 text-[#F2ECE4] h-12 text-lg flex-1"
                    data-testid="input-temperature"
                  />
                  <span className="text-2xl text-[#737373]">°C</span>
                  <Button
                    onClick={saveTemperature}
                    disabled={!temperature || savingTemp}
                    className="bg-[#88A066] hover:bg-[#7A9059] h-12 px-6"
                    data-testid="btn-save-temp"
                  >
                    {savingTemp ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                  </Button>
                </div>
                <p className="text-[10px] text-[#737373] mt-2">
                  Alerte automatique si {">"} 5°C (transmise SCAV en cas de contrôle)
                </p>
              </div>

              {/* Scanner */}
              <div className="bg-[#141414] border border-[#C1A063]/30 rounded p-4">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#C1A063] mb-2 block flex items-center gap-2">
                  <ScanLine className="w-3 h-3" /> 3 · Scanner une barquette (HID auto-focus ou caméra)
                </label>
                <form onSubmit={onScanSubmit}>
                  <div className="flex gap-2">
                    <Input
                      ref={scanFieldRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      placeholder="En attente de scan…"
                      className="bg-[#0A0A0A] border-[#C1A063]/40 text-[#F2ECE4] h-14 text-base font-mono flex-1"
                      data-testid="input-scan"
                      autoFocus
                      // Phase 87as — Empêche l'apparition du clavier virtuel Android/iOS
                      // (le scanner HID Bluetooth fournit déjà les frappes clavier)
                      inputMode="none"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                    />
                    <Button
                      type="button"
                      onClick={() => setCameraOpen(true)}
                      className="bg-[#C1A063] hover:bg-[#A78950] text-[#0A0A0A] h-14 px-4 flex-shrink-0"
                      title="Activer la caméra pour scanner"
                      data-testid="btn-open-camera"
                    >
                      <Camera className="w-5 h-5" />
                      <span className="ml-2 hidden md:inline">Caméra</span>
                    </Button>
                  </div>
                </form>
                {resolving && <p className="text-xs text-[#C1A063] mt-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Résolution…</p>}
              </div>

              {/* Card pending placement */}
              {pendingScan && (
                <div className="bg-[#141414] border-2 border-[#C1A063] rounded p-5 space-y-4" data-testid="pending-scan-card">
                  <div className="flex items-start gap-4">
                    <Package2 className="w-10 h-10 text-[#C1A063] flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-2xl leading-tight">{pendingScan.product?.name}</h3>
                      <p className="text-xs text-[#737373] uppercase tracking-wider mt-1">
                        Lot {pendingScan.lot.lot_number} · SKU {pendingScan.product?.sku}
                      </p>
                      {/* Badge DLC */}
                      {(() => {
                        const b = dlcBadge(pendingScan.lot.days_left);
                        return b ? (
                          <span className="inline-block mt-2 px-3 py-1 text-xs uppercase tracking-wider rounded-sm" style={{ color: b.color, background: b.bg }}>
                            {b.label}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  {/* Alertes */}
                  {pendingScan.alerts?.length > 0 && (
                    <div className="space-y-2">
                      {pendingScan.alerts.map((a, i) => (
                        <div key={i} className={`text-sm px-3 py-2 border-l-2 flex items-start gap-2 ${
                          a.level === "danger" ? "border-[#E54D2E] bg-[#E54D2E]/10 text-[#E54D2E]" : "border-[#C1A063] bg-[#C1A063]/10 text-[#C1A063]"
                        }`}>
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {a.msg}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Choix emplacement */}
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-2 block flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> Emplacement de pose
                      {nextFreeSlot && <span className="text-[#88A066] normal-case tracking-normal">· suggéré : E{nextFreeSlot.emp}</span>}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        ref={empFieldRef}
                        type="number"
                        inputMode="numeric"
                        value={targetEmp}
                        onChange={(e) => setTargetEmp(e.target.value)}
                        placeholder="ex: 27"
                        className="bg-[#0A0A0A] border-white/15 text-[#F2ECE4] h-14 text-2xl font-mono w-32 text-center"
                        data-testid="input-target-emp"
                      />
                      <Button
                        onClick={confirmPlacement}
                        className="bg-[#88A066] hover:bg-[#7A9059] h-14 flex-1 text-base"
                        data-testid="btn-confirm-placement"
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" /> Poser ici
                      </Button>
                      <Button
                        onClick={() => { setPendingScan(null); setTargetEmp(""); }}
                        variant="outline"
                        className="border-white/15 text-[#A6A29C] h-14 px-4"
                        data-testid="btn-cancel-placement"
                      >
                        <XCircle className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Grille emplacements */}
              {grid.length > 0 && (
                <div className="bg-[#141414] border border-white/10 rounded p-4">
                  <h3 className="text-sm uppercase tracking-[0.2em] text-[#C1A063] mb-3">État du distributeur — {grid.length} emplacements</h3>
                  <div className="grid grid-cols-7 md:grid-cols-10 gap-2">
                    {grid.map((emp) => {
                      const lotsHere = lots.filter((l) => l.emplacement_number === emp);
                      const occupied = lotsHere.length;
                      const isFull = occupied >= BARQUETTES_PER_EMP;
                      const hasDanger = lotsHere.some((l) => {
                        if (!l.dlc) return false;
                        try {
                          const dlcDate = new Date(l.dlc);
                          const now = new Date();
                          const days = Math.ceil((dlcDate - now) / 86400000);
                          return days <= 1;
                        } catch { return false; }
                      });
                      return (
                        <div
                          key={emp}
                          className="aspect-square border rounded flex flex-col items-center justify-center cursor-pointer relative group transition-all"
                          style={{
                            borderColor: hasDanger ? "#E54D2E" : isFull ? "#88A066" : occupied > 0 ? "#C1A063" : "rgba(255,255,255,0.1)",
                            background: hasDanger ? "rgba(229,77,46,0.08)" : isFull ? "rgba(136,160,102,0.08)" : occupied > 0 ? "rgba(193,160,99,0.05)" : "transparent",
                          }}
                          data-testid={`slot-${emp}`}
                        >
                          <span className="text-base font-mono">{emp}</span>
                          <span className="text-[9px] text-[#737373]">{occupied}/{BARQUETTES_PER_EMP}</span>
                          {/* Liste lots au hover */}
                          {occupied > 0 && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-[#1F1F1F] border border-white/15 rounded p-2 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity z-20 min-w-[200px] pointer-events-auto">
                              {lotsHere.map((l) => (
                                <div key={l.id} className="flex items-center justify-between gap-2 text-[11px] py-1">
                                  <span className="truncate">B{l.barquette_index} · {l.lot_number}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); markEmpty(l); }}
                                    className="text-[#E54D2E] hover:bg-[#E54D2E]/20 p-1 rounded"
                                    title="Marquer vide (écart inventaire)"
                                    data-testid={`btn-empty-${l.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-4 mt-4 text-[10px] uppercase tracking-wider text-[#737373]">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 border border-white/20" /> Vide</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 border" style={{borderColor:"#C1A063", background:"rgba(193,160,99,0.1)"}} /> Partiel</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 border" style={{borderColor:"#88A066", background:"rgba(136,160,102,0.1)"}} /> Plein</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 border" style={{borderColor:"#E54D2E", background:"rgba(229,77,46,0.1)"}} /> DLC critique</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Colonne droite : journal */}
        <aside className="lg:col-span-4 bg-[#141414] border border-white/10 rounded p-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] overflow-y-auto">
          <h3 className="text-sm uppercase tracking-[0.2em] text-[#C1A063] mb-3 flex items-center gap-2">
            <Clock className="w-3 h-3" /> Journal de visite
          </h3>
          {scanLog.length === 0 ? (
            <p className="text-xs text-[#737373] py-8 text-center">Aucune action enregistrée</p>
          ) : (
            <ul className="space-y-2" data-testid="scan-log">
              {scanLog.map((l, i) => (
                <li key={i} className="text-xs border-l-2 pl-3 py-1" style={{
                  borderColor: l.type === "error" ? "#E54D2E" : l.type === "empty" ? "#C1A063" : l.type === "temp" ? "#88A066" : "rgba(193,160,99,0.4)",
                }}>
                  <div className="text-[#F2ECE4]">{l.msg}</div>
                  <div className="text-[10px] text-[#737373] mt-0.5">{new Date(l.ts).toLocaleTimeString("fr-CH")}</div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {/* Modal scan caméra */}
      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onDetected={onCameraDetected}
      />
    </div>
  );
}
